import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const QueryResults = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  if (!state?.explanation) {
    navigate('/');
    return null;
  }

  const { explanation, practiceArea, region, attorneys, regionDropped, originalQuery } = state;

  return (
    <div style={s.page}>
      <div style={s.container}>

        {/* Back link */}
        <button onClick={() => navigate('/')} style={s.backBtn}>
          ← Вернуться на главную
        </button>

        {/* AI explanation card */}
        <div style={s.explanationCard}>
          <div style={s.aiBadge}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
            </svg>
            Анализ запроса
          </div>

          {originalQuery && (
            <p style={s.originalQuery}>«{originalQuery}»</p>
          )}

          <p style={s.explanation}>{explanation}</p>

          <div style={s.areaBadge}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
            </svg>
            Область права: <strong>{practiceArea}</strong>
            {region && !regionDropped && <> · <span>{region}</span></>}
          </div>

          {regionDropped && (
            <p style={s.regionNote}>
              Адвокатов в указанном регионе не найдено — показываем адвокатов по всему Узбекистану.
            </p>
          )}
        </div>

        {/* Results */}
        {attorneys.length > 0 ? (
          <>
            <h2 style={s.resultsTitle}>
              Адвокаты, которые могут вам помочь
              <span style={s.count}>{attorneys.length}</span>
            </h2>
            <div style={s.grid}>
              {attorneys.map(attorney => (
                <div key={attorney.id} style={s.card}
                  onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.10)'}
                  onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                >
                  <div style={s.avatar}>
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>

                  <div style={s.cardBody}>
                    <Link to={`/attorney/${attorney.id}`} style={s.nameLink}>{attorney.full_name}</Link>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.65rem' }}>
                      <span style={s.locationBadge}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/>
                        </svg>
                        {attorney.location || 'Не указан'}
                      </span>
                      {attorney.experience_label && (
                        <span style={s.licenseBadge}>В практике: {attorney.experience_label}</span>
                      )}
                    </div>

                    {attorney.practice_areas?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
                        {attorney.practice_areas.slice(0, 4).map(area => (
                          <span key={area} style={{
                            ...s.tag,
                            backgroundColor: area === practiceArea ? '#c6f6d5' : '#ebf8ff',
                            color: area === practiceArea ? '#276749' : '#2b6cb0',
                            fontWeight: area === practiceArea ? '700' : '400',
                          }}>
                            {area}
                          </span>
                        ))}
                        {attorney.practice_areas.length > 4 && <span style={s.tag}>+{attorney.practice_areas.length - 4}</span>}
                      </div>
                    )}

                    {attorney.bio && (
                      <p style={s.bio}>{attorney.bio}</p>
                    )}
                  </div>

                  <div style={s.cardAction}>
                    <button style={s.msgBtn}
                      onClick={() => navigate(`/contact/${attorney.id}`)}
                      onMouseOver={e => e.currentTarget.style.backgroundColor = '#2a5298'}
                      onMouseOut={e => e.currentTarget.style.backgroundColor = '#1a365d'}
                    >
                      Написать
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={s.moreLink}>
              <Link to="/find-attorney" state={{ practiceArea }} style={s.moreLinkAnchor}>
                Показать всех адвокатов по этой области →
              </Link>
            </div>
          </>
        ) : (
          <div style={s.empty}>
            <p style={{ color: '#718096', marginBottom: '1rem' }}>
              По вашему запросу адвокатов не найдено. Попробуйте поиск вручную.
            </p>
            <button style={s.msgBtn} onClick={() => navigate('/find-attorney', { state: { practiceArea } })}>
              Найти адвоката
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { backgroundColor: '#f7fafc', minHeight: 'calc(100vh - 58px)', padding: '2.5rem 2rem' },
  container: { maxWidth: '860px', margin: '0 auto' },
  backBtn: { background: 'none', border: 'none', color: '#718096', fontSize: '0.88rem', cursor: 'pointer', padding: '0 0 1.5rem 0', display: 'block' },
  explanationCard: { backgroundColor: 'white', borderRadius: '14px', padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0', marginBottom: '2.5rem' },
  aiBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', fontWeight: '700', color: '#553c9a', backgroundColor: '#faf5ff', padding: '0.25rem 0.7rem', borderRadius: '20px', marginBottom: '1rem' },
  originalQuery: { fontSize: '0.92rem', color: '#718096', fontStyle: 'italic', marginBottom: '1rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1rem' },
  explanation: { fontSize: '1rem', color: '#2d3748', lineHeight: '1.7', marginBottom: '1.25rem' },
  areaBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', color: '#2b6cb0', backgroundColor: '#ebf8ff', padding: '0.4rem 0.9rem', borderRadius: '20px' },
  regionNote: { marginTop: '0.75rem', fontSize: '0.83rem', color: '#c05621' },
  resultsTitle: { fontSize: '1.4rem', fontWeight: '700', color: '#1a365d', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' },
  count: { backgroundColor: '#1a365d', color: 'white', fontSize: '0.8rem', fontWeight: '700', padding: '0.15rem 0.6rem', borderRadius: '20px' },
  grid: { display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', gap: '1.25rem', alignItems: 'flex-start', transition: 'box-shadow 0.2s' },
  avatar: { width: '70px', height: '90px', borderRadius: '8px', backgroundColor: '#e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0aec0', flexShrink: 0 },
  cardBody: { flex: 1, minWidth: 0 },
  nameLink: { fontSize: '1.1rem', fontWeight: '700', color: '#2b6cb0', textDecoration: 'none', display: 'inline-block', marginBottom: '0.4rem' },
  locationBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.83rem', color: '#718096' },
  licenseBadge: { fontSize: '0.78rem', color: '#2b6cb0', backgroundColor: '#ebf8ff', padding: '0.12rem 0.55rem', borderRadius: '20px' },
  tag: { padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.78rem' },
  bio: { color: '#4a5568', fontSize: '0.88rem', lineHeight: '1.55', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  cardAction: { flexShrink: 0, display: 'flex', alignItems: 'center' },
  msgBtn: { backgroundColor: '#1a365d', color: 'white', border: 'none', padding: '0.6rem 1.1rem', borderRadius: '8px', fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  moreLink: { textAlign: 'center', padding: '1rem 0' },
  moreLinkAnchor: { color: '#3182ce', textDecoration: 'none', fontSize: '0.95rem', fontWeight: '500' },
  empty: { textAlign: 'center', padding: '3rem', backgroundColor: 'white', borderRadius: '12px' },
};

export default QueryResults;
